import { z } from "zod";

/**
 * ICD v1 message schemas (device <-> cloud).
 * Source of truth: docs/icd (FTS Cihaz Haberleşme Rehberi ve ICD v0.1).
 */

export const ICD_VERSION = 1 as const;

const isoUtc = z
  .string()
  .datetime({ offset: false, precision: undefined })
  .describe("UTC timestamp, ISO 8601, e.g. 2026-11-04T08:15:30.250Z");

const uuid = z.string().uuid();

/** Integer bounds matching the database column types, so a faulty device cannot create poison messages. */
const INT32_MAX = 2_147_483_647;
const seqNo = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const version = z.number().int().nonnegative().max(INT32_MAX);

/** Card UID as hex string (reader-specific byte order is defined per reader type in the ICD). */
export const cardUid = z.string().regex(/^[0-9A-F]{8,20}$/, "card UID must be upper-case hex, 8-20 chars");

export const position = z.object({
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  acc: z.number().nonnegative().max(1e6).optional(),
  src: z.enum(["gnss", "wifi", "ble"]),
  sat: z.number().int().nonnegative().max(255).optional(),
});
export type Position = z.infer<typeof position>;

/**
 * Data link the device uses to reach the cloud. Devices work over GSM and/or Wi-Fi:
 * "gsm" = 2G/3G cellular, "lte" = 4G/LTE (incl. LTE-M) cellular, "wifi" = site Wi-Fi.
 * The device picks whichever is available; the cloud is link-agnostic (MQTT over TLS on either).
 */
export const LINK_TYPES = ["gsm", "lte", "wifi"] as const;
export const linkType = z.enum(LINK_TYPES);
export type LinkType = z.infer<typeof linkType>;

/**
 * Optional field that is dropped (undefined) instead of failing the message when it is invalid (ADR 0014
 * rule for optional telemetry fields; ICD v0.2 section 9 applies it to the battery and lift modules).
 * decodeUplink reports every dropped field as a warning.
 */
const lenient = <T extends z.ZodTypeAny>(s: T) => s.optional().catch(undefined);

/* ---------------- ICD v0.2 section 9: battery and lift modules (ADR 0012, ADR 0017) ---------------- */

/**
 * Measurement ranges (ICD v0.2 section 9): battery 24-96 V nominal, 0-120 V measured; current ±500 A
 * continuous / ±1000 A peak; temperature -20..+80 degC; lift pressure sensor 0-250 bar. Values outside
 * a range are sensor faults and are dropped. Ranges the ICD does not give (fork height, load, amp-hours,
 * energy, cycle length) are generous plausibility bounds of the cloud.
 */
export const MODULE_LIMITS = {
  batV: { min: 0, max: 120 },
  /** Sign (ICD v0.2 section 9): + charge, - discharge. */
  batA: { min: -1000, max: 1000 },
  batT: { min: -20, max: 80 },
  soc: { min: 0, max: 100 },
  liftBar: { min: 0, max: 250 },
  forkMm: { min: 0, max: 20_000 },
  loadKg: { min: 0, max: 50_000 },
  liftDurMs: { min: 0, max: 600_000 },
  ah: { min: 0, max: 5_000 },
  kwh: { min: 0, max: 500 },
} as const;
const between = (l: { min: number; max: number }) => z.number().min(l.min).max(l.max);
const intBetween = (l: { min: number; max: number }) => z.number().int().min(l.min).max(l.max);

export const MODULE_TYPES = ["battery", "lift"] as const;
export const BATTERY_TYPES = ["lead_acid", "lithium"] as const;
export type BatteryType = (typeof BATTERY_TYPES)[number];
export type ModuleType = (typeof MODULE_TYPES)[number];
/** Nominal traction battery voltages (same list as vehicles.battery_voltage). */
export const NOMINAL_VOLTAGES = [12, 24, 36, 48, 72, 80, 96] as const;

/** One measurement module announced in hello.modules. Unknown keys are ignored. */
export const deviceModule = z.object({
  type: z.enum(MODULE_TYPES),
  fw: z.string().min(1).max(32).optional(),
  sn: z.string().min(1).max(32).optional(),
  /** Battery module: nominal voltage of the battery it measures. */
  nominal_v: z
    .number()
    .int()
    .refine((v) => (NOMINAL_VOLTAGES as readonly number[]).includes(v), "unknown nominal voltage")
    .optional(),
  /** Lift module: a fork height sensor is fitted (otherwise fork_mm is never sent). */
  fork: z.boolean().optional(),
});
export type DeviceModule = z.infer<typeof deviceModule>;
export const MAX_MODULES = 4;

/**
 * hello.modules: list of fitted modules. Contract (ICD v0.2 section 9): ["battery","lift"]; the object form
 * [{"type":"battery","fw":"1.0.3"}] is also accepted leniently.
 * Lenient: a non-list is dropped, invalid or repeated entries are skipped (first one wins), so a faulty
 * announcement never loses the hello. An empty list means "no module fitted".
 */
export function normalizeModules(raw: unknown): DeviceModule[] | undefined {
  if (!Array.isArray(raw) || raw.length > 32) return undefined;
  const out: DeviceModule[] = [];
  for (const item of raw) {
    const r = deviceModule.safeParse(typeof item === "string" ? { type: item } : item);
    if (r.success && !out.some((m) => m.type === r.data.type)) out.push(r.data);
    if (out.length >= MAX_MODULES) break;
  }
  return out;
}

/**
 * Flat extra fields of the module events (ICD v0.2 section 9), per event type. Every field lenient.
 * The device is authoritative: lift cycles, load estimates, charge sessions, deep discharge and
 * overtemperature are detected on the device.
 */
export const MODULE_EVENT_FIELDS = {
  lift: ["dur_ms", "peak_bar", "load_kg", "max_fork_mm"],
  overload: ["peak_bar", "load_kg"],
  charge_start: ["soc", "ah", "kwh"],
  charge_end: ["soc", "ah", "kwh"],
  deep_discharge: ["soc", "bat_v"],
  bat_overtemp: ["bat_t"],
} as const;
const f = {
  dur_ms: lenient(intBetween(MODULE_LIMITS.liftDurMs)),
  peak_bar: lenient(between(MODULE_LIMITS.liftBar)),
  load_kg: lenient(between(MODULE_LIMITS.loadKg)),
  max_fork_mm: lenient(intBetween(MODULE_LIMITS.forkMm)),
  soc: lenient(between(MODULE_LIMITS.soc)),
  ah: lenient(between(MODULE_LIMITS.ah)),
  kwh: lenient(between(MODULE_LIMITS.kwh)),
  bat_v: lenient(between(MODULE_LIMITS.batV)),
  bat_t: lenient(between(MODULE_LIMITS.batT)),
};

/** Fields present on every device -> cloud message. */
const deviceEnvelope = {
  v: z.literal(ICD_VERSION),
  mid: uuid,
  seq: seqNo,
  ts: isoUtc,
  buf: z.boolean().default(false),
  ts_ok: z.boolean().default(true),
};

export const helloMessage = z.object({
  ...deviceEnvelope,
  hw: z.string().min(1),
  fw: z.string().min(1),
  imei: z.string().regex(/^\d{15}$/).optional(),
  iccid: z.string().regex(/^\d{18,22}$/).optional(),
  authlist_ver: version.optional(),
  /** Lenient: a bad value (string, negative, fraction, > INT32) is dropped with a warning; the hello is kept. */
  config_ver: lenient(version),
  /** Links the hardware supports (fitted modules), e.g. ["gsm","wifi"]. Optional for older firmware. */
  links: z
    .array(linkType)
    .min(1)
    .max(LINK_TYPES.length)
    .refine((a) => new Set(a).size === a.length, "links must not repeat")
    .optional(),
  /** Fitted measurement modules (ICD v0.2 section 9). Absent = not reported (older firmware). Lenient. */
  modules: z.unknown().transform(normalizeModules).optional(),
});
export type HelloMessage = z.infer<typeof helloMessage>;

/**
 * Adaptive reporting (ADR 0014): the device samples its position every second; a telemetry message
 * sent while moving carries the samples since the previous message. `dt_ms` is the age of the
 * sample relative to the message `ts` (0 = at `ts`). telemetry_moving_s is at most 10, so the
 * samples of one interval always fit in TRACK_MAX_POINTS; dt_ms is bounded generously at one minute.
 */
export const TRACK_MAX_POINTS = 10;
export const TRACK_MAX_DT_MS = 60_000;
export const trackPoint = z.object({
  dt_ms: z.number().int().min(0).max(TRACK_MAX_DT_MS),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  spd: z.number().nonnegative().max(500).optional(),
  /** Heading in degrees, 0 = north, clockwise. */
  hdg: z.number().min(0).lt(360).optional(),
});
export type TrackPoint = z.infer<typeof trackPoint>;

const state3 = z.number().int().min(0).max(7);
const state4 = z.number().int().min(0).max(15);
/** cam: channels 1–16, ipc: 1–8 (3-bit states); sto: main storage states; vloss: video-loss bitmap (bit 0 = ch 1). */
export const dvrHealth = z.object({
  cam: z.array(state3).max(16).optional(),
  ipc: z.array(state3).max(8).optional(),
  sto: z.object({ ssd: state4.optional(), sd: state4.optional(), usb: state4.optional(), usb2: state4.optional() }).strict().optional(),
  vloss: z.number().int().min(0).max(0xffffffff).optional(),
});
export type DvrHealth = z.infer<typeof dvrHealth>;

export const telemetryMessage = z.object({
  ...deviceEnvelope,
  /** Newest position; when `pts` is present it equals the newest sample. */
  pos: position.optional(),
  /**
   * 1-s samples since the previous message, oldest first (ADR 0014). Omitted when it would only repeat `pos`.
   * Lenient: an invalid, empty or oversized track is dropped (undefined) and the rest of the message is
   * accepted. Ingest acknowledges every delivery, so rejecting here would lose the whole message for good.
   */
  pts: z.array(trackPoint).min(1).max(TRACK_MAX_POINTS).optional().catch(undefined),
  spd: z.number().nonnegative().max(500).optional(),
  ign: z.boolean(),
  seat: z.boolean().optional(),
  belt: z.boolean().optional(),
  op: cardUid.nullable().optional(),
  /** Link in use when the message was produced; rssi is the signal strength of that link (dBm). */
  link: linkType.optional(),
  rssi: z.number().int().min(-200).max(0).optional(),
  vin: z.number().nonnegative().max(1000).optional(),
  /*
   * Battery module (ICD v0.2 section 9), omitted when the module is absent. Optional and lenient: an
   * invalid value is dropped, never the message. soc is computed on the device (Ah integration and
   * rest-voltage correction, battery type and capacity from config).
   */
  bat_v: lenient(between(MODULE_LIMITS.batV)),
  /** + charge, - discharge (ICD v0.2 section 9). */
  bat_a: lenient(between(MODULE_LIMITS.batA)),
  bat_t: lenient(between(MODULE_LIMITS.batT)),
  soc: lenient(between(MODULE_LIMITS.soc)),
  /* Lift module: instant lift pressure (sensor 0-250 bar) and fork height. */
  lift_bar: lenient(between(MODULE_LIMITS.liftBar)),
  fork_mm: lenient(intBetween(MODULE_LIMITS.forkMm)),
  /*
   * Recorder health (ADR 0027): camera channel and storage states of a bridged m-DVR (JT/T 808 additional
   * information 0xEF / 0x15). Optional and lenient: an invalid block is dropped, never the message.
   */
  dvr: lenient(dvrHealth),
  /*
   * Units without our modules (Teltonika, ADR 0034): raw analog inputs (mV, 0-30 V), movement sensor and the
   * unit's immobiliser / authorisation state. What an analog input means is a per-vehicle calibration in the
   * cloud. Optional and lenient: an invalid value is dropped, never the message.
   */
  ain1_mv: lenient(z.number().int().min(0).max(30_000)),
  ain2_mv: lenient(z.number().int().min(0).max(30_000)),
  mov: lenient(z.boolean()),
  /** Immobiliser state of the unit (it only prevents the FIRST start without a card; ADR 0003). */
  immo: lenient(z.enum(["no_card", "card", "authorized"])),
});
export type TelemetryMessage = z.infer<typeof telemetryMessage>;

const eventBase = {
  ...deviceEnvelope,
  op: cardUid.nullable().optional(),
  pos: position.optional(),
};

export const impactEvent = z.object({
  ...eventBase,
  type: z.literal("impact"),
  /**
   * peak_g, axis and dur_ms are required by the ICD for our own telematics unit. They are optional in the
   * schema only because bridged third-party units (ADR 0021, JT/T 808 collision alarm bit) report that an
   * impact happened without its magnitude; the bridge never invents them.
   */
  impact: z.object({
    peak_g: z.number().positive().max(1000).optional(),
    axis: z.enum(["x", "y", "z"]).optional(),
    dur_ms: z.number().int().nonnegative().max(INT32_MAX).optional(),
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  }),
});

export const cardEvent = z.object({
  ...eventBase,
  type: z.enum(["card_ok", "card_denied"]),
  card: cardUid,
  reason: z.enum(["unknown", "expired", "revoked"]).optional(),
});

export const simpleEvent = z.object({
  ...eventBase,
  type: z.enum(["logout", "seat_violation", "belt_violation", "tamper", "power_loss", "power_restore"]),
});

/**
 * Module events with flat extra fields (ICD v0.2 section 9). `lift`: one lift cycle (starts when the
 * pressure exceeds lift_bar_threshold, ends when it drops; load estimated on the device with
 * lift_kg_per_bar). `overload`: a separate event, sent in addition to the cycle's `lift`.
 * `charge_start`/`charge_end`: detected on the device from the current direction and its duration.
 */
export const liftEvent = z.object({ ...eventBase, type: z.literal("lift"), dur_ms: f.dur_ms, peak_bar: f.peak_bar, load_kg: f.load_kg, max_fork_mm: f.max_fork_mm });
export const overloadEvent = z.object({ ...eventBase, type: z.literal("overload"), peak_bar: f.peak_bar, load_kg: f.load_kg });
export const chargeEvent = z.object({ ...eventBase, type: z.enum(["charge_start", "charge_end"]), soc: f.soc, ah: f.ah, kwh: f.kwh });
export const deepDischargeEvent = z.object({ ...eventBase, type: z.literal("deep_discharge"), soc: f.soc, bat_v: f.bat_v });
export const batOvertempEvent = z.object({ ...eventBase, type: z.literal("bat_overtemp"), bat_t: f.bat_t });
/**
 * Eco-driving events of an accelerometer unit (Teltonika green driving, ADR 0034): harsh acceleration, braking
 * and cornering with the measured value in g (lenient). Counted in the driving report; never an alarm and
 * never a command (ADR 0003).
 */
export const ecoEvent = z.object({ ...eventBase, type: z.enum(["harsh_accel", "harsh_brake", "harsh_corner"]), g: lenient(z.number().positive().max(16)) });

export const eventMessage = z.discriminatedUnion("type", [
  impactEvent,
  cardEvent.extend({ type: z.literal("card_ok") }),
  cardEvent.extend({ type: z.literal("card_denied") }),
  simpleEvent.extend({ type: z.literal("logout") }),
  simpleEvent.extend({ type: z.literal("seat_violation") }),
  simpleEvent.extend({ type: z.literal("belt_violation") }),
  simpleEvent.extend({ type: z.literal("tamper") }),
  simpleEvent.extend({ type: z.literal("power_loss") }),
  simpleEvent.extend({ type: z.literal("power_restore") }),
  liftEvent,
  overloadEvent,
  chargeEvent.extend({ type: z.literal("charge_start") }),
  chargeEvent.extend({ type: z.literal("charge_end") }),
  deepDischargeEvent,
  batOvertempEvent,
  ecoEvent.extend({ type: z.literal("harsh_accel") }),
  ecoEvent.extend({ type: z.literal("harsh_brake") }),
  ecoEvent.extend({ type: z.literal("harsh_corner") }),
]);
export type EventMessage = z.infer<typeof eventMessage>;
export type EventType = EventMessage["type"];
export const EVENT_TYPES = [
  "impact",
  "card_ok",
  "card_denied",
  "logout",
  "seat_violation",
  "belt_violation",
  "tamper",
  "power_loss",
  "power_restore",
  // ICD v0.2 section 9 (ADR 0012).
  "lift",
  "overload",
  "charge_start",
  "charge_end",
  "deep_discharge",
  "bat_overtemp",
  // ADR 0034: eco-driving events of accelerometer units.
  "harsh_accel",
  "harsh_brake",
  "harsh_corner",
] as const satisfies readonly EventType[];
export const LIFT_EVENT_TYPES = ["lift", "overload"] as const satisfies readonly EventType[];
export const BATTERY_EVENT_TYPES = ["charge_start", "charge_end", "deep_discharge", "bat_overtemp"] as const satisfies readonly EventType[];

export const ackMessage = z.object({
  ...deviceEnvelope,
  ack: uuid,
  ok: z.boolean(),
  ver: version.optional(),
  err: z.string().max(200).optional(),
});
export type AckMessage = z.infer<typeof ackMessage>;

/** Broker Last Will / online notice. Not produced by device firmware logic, so no envelope. */
export const statusMessage = z.object({
  state: z.enum(["online", "offline"]),
});
export type StatusMessage = z.infer<typeof statusMessage>;

/* ---------------- cloud -> device commands ---------------- */

const cmdEnvelope = {
  v: z.literal(ICD_VERSION),
  mid: uuid,
};

export const authEntry = z.object({
  uid: cardUid,
  until: z.string().date().optional(),
});
export type AuthEntry = z.infer<typeof authEntry>;

export const authlistFullCommand = z.object({
  ...cmdEnvelope,
  cmd: z.literal("authlist"),
  mode: z.literal("full"),
  ver: version.min(1),
  entries: z.array(authEntry).max(5000),
});

export const authlistDeltaCommand = z.object({
  ...cmdEnvelope,
  cmd: z.literal("authlist"),
  mode: z.literal("delta"),
  ver: version.min(1),
  base: version,
  add: z.array(authEntry).default([]),
  remove: z.array(cardUid).default([]),
});

/**
 * Remote configuration. By design (ADR 0003) there is NO parameter that links an
 * impact event to the ignition relay; `.strict()` rejects any unknown key.
 *
 * Reporting cadence (ADR 0014): telemetry_moving_s / telemetry_idle_s win over the older
 * telemetry_on_s when both are given (telemetry_on_s alone sets both). telemetry_off_s keeps its
 * meaning (interval with the ignition off, the heartbeat) with the ADR 0014 bounds.
 */
export const configCommand = z
  .object({
    ...cmdEnvelope,
    cmd: z.literal("config"),
    ver: version.min(1),
    /** Legacy: interval with the ignition on. Superseded by telemetry_moving_s / telemetry_idle_s. */
    telemetry_on_s: z.number().int().min(1).max(3600).optional(),
    /**
     * Heartbeat with the ignition off. Default 300 s; max 540 s so a parked vehicle always reports within
     * the 10-minute offline rule. BREAKING vs. ICD v0.1: bounds were 10..86400.
     */
    telemetry_off_s: z.number().int().min(60).max(540).optional(),
    /** Interval while moving. Default 3 s; max 10 s so the 1-s samples of one interval fit in `pts`. */
    telemetry_moving_s: z.number().int().min(1).max(10).optional(),
    /** Interval with the ignition on and the vehicle standing. Default 30 s. */
    telemetry_idle_s: z.number().int().min(5).max(600).optional(),
    /** While moving, report at once after this distance (m) from the last reported position. Default 5. */
    move_dist_m: z.number().int().min(1).max(100).optional(),
    /** While moving, report at once after this heading change (degrees). Default 15. */
    move_heading_deg: z.number().int().min(5).max(90).optional(),
    impact_g: z
      .object({ l1: z.number().positive(), l2: z.number().positive(), l3: z.number().positive() })
      .refine((t) => t.l1 < t.l2 && t.l2 < t.l3, "impact thresholds must increase l1 < l2 < l3")
      .optional(),
    session_timeout_s: z.number().int().min(30).max(86400).optional(),
    wifi: z.array(z.object({ ssid: z.string().min(1).max(32), psk: z.string().min(8).max(63) })).max(8).optional(),
    motion_kmh: z.number().nonnegative().max(30).optional(),
    /**
     * Battery and lift modules (ICD v0.2 section 9), flat. bat_type and bat_ah feed the device's SoC
     * computation; lift_kg_per_bar is the per-vehicle calibration of the device's load estimate;
     * lift_bar_threshold starts a lift cycle. Measuring and reporting only: nothing here can act on the
     * relay, driving or lifting (ADR 0003, ADR 0012). Bounds of bat_ah, lift_kg_per_bar and
     * lift_bar_threshold are cloud plausibility checks (the ICD gives none; threshold within the 0-250 bar sensor).
     */
    bat_type: z.enum(BATTERY_TYPES).optional(),
    bat_ah: z.number().int().min(10).max(5000).optional(),
    lift_kg_per_bar: z.number().positive().max(1000).optional(),
    lift_bar_threshold: z.number().positive().max(250).optional(),
  })
  .strict();

export const otaCommand = z.object({
  ...cmdEnvelope,
  cmd: z.literal("ota"),
  fw: z.string().min(1),
  url: z.string().url().startsWith("https://"),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
});

export const timeCommand = z.object({
  ...cmdEnvelope,
  cmd: z.literal("time"),
  now: isoUtc,
});

/**
 * "Live watch" (ADR 0014): report every second for `dur_s` seconds, then return to the configured
 * cadence. A new live command restarts the period. Sent NOT retained (it is only meaningful now).
 * ADR 0003: this command changes the reporting cadence only; it never touches the relay or ignition.
 */
export const LIVE_WATCH = { minS: 60, maxS: 1800, defaultS: 600, intervalS: 1, minGapS: 30, maxPerTenant: 5 } as const;
export const liveCommand = z
  .object({
    ...cmdEnvelope,
    cmd: z.literal("live"),
    dur_s: z.number().int().min(LIVE_WATCH.minS).max(LIVE_WATCH.maxS),
  })
  .strict();

export const commandMessage = z.union([
  authlistFullCommand,
  authlistDeltaCommand,
  configCommand,
  otaCommand,
  timeCommand,
  liveCommand,
]);
export type CommandMessage = z.infer<typeof commandMessage>;
export type AuthlistCommand = z.infer<typeof authlistFullCommand> | z.infer<typeof authlistDeltaCommand>;
export type ConfigCommand = z.infer<typeof configCommand>;
export type LiveCommand = z.infer<typeof liveCommand>;
